import { useMemo } from 'react';
import type {
  EstimateRecurringConfig,
  EstimateRecurringEndType,
  EstimateRecurringFrequency,
  EstimateRecurringIntervalUnit,
} from '@/types/estimate';
import type { TaxResolutionResult } from '@/types/tax';
import type { EditableLineItem } from '@/routes/_dashboard/dashboard/estimates/_shared';
import type { EstimateMembershipInfo } from './useEstimateMembershipInfo';
import { computeEstimatePricingPreview } from '@/lib/sales/estimate-pricing-preview';
import { useMembershipOfferPreview } from './useMembershipOfferPreview';
import { isMembershipLine } from '@notifi/shared-types/membership-line';

interface UseEstimateFinancialCalculationsArgs {
  lineItems: EditableLineItem[];
  taxResolution: TaxResolutionResult | null;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  discountValue: number;
  discountType: '$' | '%';
  membershipInfo: EstimateMembershipInfo | null;
  depositValue: number;
  depositType: '$' | '%';
  bookingType: 'ONE_OFF' | 'RECURRING';
  recurringFrequency: EstimateRecurringFrequency;
  recurringInterval: number;
  recurringIntervalUnit: EstimateRecurringIntervalUnit;
  recurringStartDate: string;
  recurringDaysOfWeek: number[];
  recurringEndType: EstimateRecurringEndType;
  recurringEndAfterCount: number;
  recurringEndOnDate: string;
  /** NOT-628 — whose home the join-and-save offer should be priced for. */
  membershipCustomerId?: string | null;
  membershipServiceAddressId?: string | null;
  /** Estimate authoring opt-in; other consumers of this shared hook stay quiet. */
  membershipOfferEnabled?: boolean;
  /** Active plan identities, excluding a merely prospective in-cart plan. */
  ownedMembershipPlanIds?: readonly string[];
  /** Benefits already active on this estimate before the candidate is added. */
  membershipOfferBaselineInfo?: EstimateMembershipInfo | null;
}

export function useEstimateFinancialCalculations({
  lineItems,
  taxResolution,
  pricingMode,
  discountValue,
  discountType,
  membershipInfo,
  depositValue,
  depositType,
  bookingType,
  recurringFrequency,
  recurringInterval,
  recurringIntervalUnit,
  recurringStartDate,
  recurringDaysOfWeek,
  recurringEndType,
  membershipCustomerId,
  membershipServiceAddressId,
  membershipOfferEnabled = false,
  ownedMembershipPlanIds = [],
  membershipOfferBaselineInfo = null,
  recurringEndAfterCount,
  recurringEndOnDate,
}: UseEstimateFinancialCalculationsArgs) {
  const pricingPreview = useMemo(
    () =>
      computeEstimatePricingPreview({
        lineItems,
        taxResolution,
        pricingMode,
        discountValue,
        discountType,
        membershipInfo,
      }),
    [lineItems, taxResolution, pricingMode, discountValue, discountType, membershipInfo],
  );

  const {
    lineItemPreviews,
    subtotal,
    taxTotal,
    taxBreakdown,
    discountAmount,
    membershipDiscountPreview,
    membershipEligibleRowIds,
    grandTotal,
    depositEligibleTotal,
  } = pricingPreview;

  // Once the author adds a recommendation, constrain discovery to that exact
  // optional plan. The exclusion set below then suppresses the card instead of
  // falling through to a second plan while one is already in the estimate.
  const optionalMembershipOfferIdentity = useMemo(() => {
    const line = lineItems.find(
      (line) =>
        isMembershipLine(line) &&
        line.isOptional === true &&
        line.isSelected === false &&
        Boolean(line.membershipPlanId),
    );
    return line?.membershipPlanId
      ? {
          planId: line.membershipPlanId,
          billingOptionId: line.membershipBillingOptionId ?? null,
        }
      : null;
  }, [lineItems]);
  const excludedOfferPlanIds = useMemo(
    () => [
      ...new Set([
        ...ownedMembershipPlanIds,
        ...lineItems
          // An unselected optional line IS the authored offer. It does not
          // apply benefits yet, so keep it eligible for the savings preview.
          // Required/selected lines do apply benefits and must suppress the
          // duplicate recommendation.
          .filter((line) => isMembershipLine(line) && line.isSelected !== false)
          .map((line) => line.membershipPlanId)
          .filter((planId): planId is string => Boolean(planId)),
      ]),
    ],
    [lineItems, ownedMembershipPlanIds],
  );
  const offerInputs = useMemo(
    () => ({ lineItems, taxResolution, pricingMode, discountValue, discountType }),
    [lineItems, taxResolution, pricingMode, discountValue, discountType],
  );
  const membershipOffer = useMembershipOfferPreview({
    inputs: offerInputs,
    enabled: membershipOfferEnabled,
    planId: optionalMembershipOfferIdentity?.planId,
    billingOptionId: optionalMembershipOfferIdentity?.billingOptionId,
    customerId: membershipCustomerId,
    serviceAddressId: membershipServiceAddressId,
    excludedPlanIds: excludedOfferPlanIds,
    baselineMembershipInfo: membershipOfferBaselineInfo,
  });

  const recurringConfigPayload = useMemo<EstimateRecurringConfig | null>(() => {
    if (bookingType !== 'RECURRING') return null;
    if (!recurringStartDate) return null;

    const config: EstimateRecurringConfig = {
      frequency: recurringFrequency,
      startDate: recurringStartDate,
      endType: recurringEndType,
    };

    if (recurringFrequency === 'CUSTOM') {
      config.interval = recurringInterval;
      config.intervalUnit = recurringIntervalUnit;
    }
    // NOT-240: carry selected weekdays for weekly/biweekly/custom-by-weeks.
    const weeklyDaysApplicable =
      recurringFrequency === 'WEEKLY' ||
      recurringFrequency === 'BIWEEKLY' ||
      (recurringFrequency === 'CUSTOM' && recurringIntervalUnit === 'WEEKS');
    if (weeklyDaysApplicable && recurringDaysOfWeek.length > 0) {
      config.customDays = [...recurringDaysOfWeek].sort((a, b) => a - b);
    }
    if (recurringEndType === 'AFTER_COUNT' && recurringEndAfterCount > 0) {
      config.endAfterCount = recurringEndAfterCount;
    }
    if (recurringEndType === 'ON_DATE' && recurringEndOnDate) {
      config.endOnDate = recurringEndOnDate;
    }

    return config;
  }, [
    bookingType,
    recurringFrequency,
    recurringInterval,
    recurringIntervalUnit,
    recurringStartDate,
    recurringDaysOfWeek,
    recurringEndType,
    recurringEndAfterCount,
    recurringEndOnDate,
  ]);

  // NOT-604: the deposit is charged against the deposit-eligible total
  // (grandTotal minus any membership line), matching the backend, so a
  // membership in the cart never inflates the deposit shown on the form.
  const depositAmount = useMemo(() => {
    if (depositValue <= 0) return 0;
    // Both branches clamp the way the server's deposit engine does, so a
    // stored oversized value (from before the form refused one) previews the
    // figure the PDF will actually print.
    return depositType === '%'
      ? Math.round(depositEligibleTotal * (Math.min(100, depositValue) / 100) * 100) / 100
      : Math.min(depositValue, depositEligibleTotal);
  }, [depositValue, depositType, depositEligibleTotal]);

  return {
    lineItemPreviews,
    subtotal,
    taxTotal,
    taxBreakdown,
    discountAmount,
    recurringConfigPayload,
    membershipDiscountPreview,
    membershipEligibleRowIds,
    membershipOffer,
    grandTotal,
    depositEligibleTotal,
    depositAmount,
  };
}
